import type { MediaAccessState } from './useDevicePreview';

export type MediaDeviceOption = { deviceId: string; label: string };

/** A call that needs audio refuses to start without a microphone rather than join silent. */
export const NO_MICROPHONE_TEXT = 'No microphone available — allow microphone access to join.';

/**
 * Until the page holds camera/mic permission, browsers report a single placeholder
 * entry per kind with an empty deviceId and no label. Those are not selectable.
 */
function realDevices(devices: MediaDeviceInfo[]): MediaDeviceInfo[] {
  return devices.filter((d) => d.deviceId);
}

export function toDeviceOptions(devices: MediaDeviceInfo[], kind: string): MediaDeviceOption[] {
  return realDevices(devices).map((d, i) => ({
    deviceId: d.deviceId,
    label: d.label || `${kind} ${i + 1}`,
  }));
}

/** Keep the current selection if it still exists, else follow the system default. */
export function pickDefaultDevice(
  devices: MediaDeviceInfo[],
  previous: string | undefined,
): string | undefined {
  const usable = realDevices(devices);
  if (previous && usable.some((d) => d.deviceId === previous)) {
    return previous;
  }
  return usable.find((d) => d.deviceId === 'default')?.deviceId ?? usable[0]?.deviceId;
}

/**
 * Whether the preview may join a call that needs audio. Disabled while the permission prompt is still
 * open (no device chosen yet) and whenever no microphone is selected; once access was granted without a
 * microphone, `notice` says so (a blocked or unsupported browser already shows its own error).
 */
export function microphoneJoinState(
  access: MediaAccessState,
  audioDeviceId: string | undefined,
): { disabled: boolean; notice: string | null } {
  if (access === 'pending' || access === 'blocked') return { disabled: true, notice: null };
  if (!audioDeviceId) return { disabled: true, notice: access === 'granted' ? NO_MICROPHONE_TEXT : null };
  return { disabled: false, notice: null };
}
